import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../components/layout/DashboardLayout';
import usePageTitle from '../hooks/usePageTitle';
import useSmartRefresh from '../hooks/useSmartRefresh';
import { SupportModal } from '../components/modals';
import { RefreshButton, SummaryCard, ProfessionalBarChart, ChartContainer } from '../components/ui';
import dashboardService from '../services/dashboardService';

/**
 * Dashboard Home Page - Integrated with Backend
 */
const DashboardHomePage = () => {
  // Set page title
  usePageTitle('Dashboard');

  const navigate = useNavigate();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  // Fetch dashboard data from backend
  const fetchDashboardData = useCallback(async (period) => {
    try {
      setIsLoading(true);
      const response = await dashboardService.getUserDashboardStats(period);
      
      if (response.success) {
        setDashboardStats(response.stats);
      } else {
        toast.error(response.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array since the function doesn't depend on any state

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod);
    fetchDashboardData(newPeriod);
  };

  // Load data on mount and when period changes
  useEffect(() => {
    fetchDashboardData(selectedPeriod);
  }, [selectedPeriod, fetchDashboardData]);

  const { isRefreshing, refreshData } = useSmartRefresh(
    () => fetchDashboardData(selectedPeriod),
    'Dashboard refreshed successfully'
  );

  const handleInvoicesClick = () => {
    navigate('/dashboard/invoices');
  };

  const handleSupportClick = () => {
    setIsSupportModalOpen(true);
  };

  // Prepare stats data from backend response
  const statsData = dashboardStats ? [
    { 
      id: 1,
      label: 'Credit Balance', 
      value: `$${dashboardStats.subscription?.credit_balance?.toFixed(2) || '0.00'}`, 
      subtext: dashboardStats.subscription?.plan_name || 'No Plan', 
      change: dashboardStats.subscription?.status === 'active' ? 'Active' : 'Inactive',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H4.5m-1.5 0H21m0 0v.375c0 .621-.504 1.125-1.125 1.125H19.5m1.5-1.5H3.75m15.75 0v11.25c0 .621-.504 1.125-1.125 1.125h-1.5a1.125 1.125 0 01-1.125-1.125V15.75m-7.5 0V18c0 .414-.336.75-.75.75h-9m18-3.75h-7.5V18c0 .414-.336.75-.75.75h-9M12 9.75h3.75m-3.75 0h-8.25m0 0V6a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121.75 6v3.75m-8.25 0V18c0 .414-.336.75-.75.75h-9" />
        </svg>
      ),
      bgGradient: 'from-emerald-50 to-teal-50',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    },
    { 
      id: 2,
      label: 'Total Orders', 
      value: dashboardStats.orders?.total_orders?.toString() || '0', 
      subtext: 'All time', 
      change: `$${dashboardStats.orders?.total_spent?.toFixed(2) || '0.00'} spent`,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
        </svg>
      ),
      bgGradient: 'from-blue-50 to-indigo-50',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    },
  ] : [];

  const orderStats = dashboardStats ? [
    { 
      label: 'Total Orders', 
      value: dashboardStats.orders?.total_orders || 0, 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
        </svg>
      ),
      bgColor: 'bg-gray-50',
      iconColor: 'text-gray-600 dark:text-gray-400'
    },
    { 
      label: 'Successful', 
      value: dashboardStats.orders?.successful_orders || 0, 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgColor: 'bg-success-50',
      iconColor: 'text-success-600'
    },
    { 
      label: 'Failed', 
      value: dashboardStats.orders?.failed_orders || 0, 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgColor: 'bg-error-50',
      iconColor: 'text-error-600'
    },
    { 
      label: 'Pending', 
      value: dashboardStats.orders?.pending_orders || 0, 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgColor: 'bg-warning-50',
      iconColor: 'text-warning-600'
    },
  ] : [];

  return (
    <>
      <SupportModal 
        isOpen={isSupportModalOpen} 
        onClose={() => setIsSupportModalOpen(false)} 
      />
      <DashboardLayout pageName="Dashboard">
      <div className="space-y-8">
        {/* Header with Refresh Button */}
        <motion.div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-4 sm:mb-0">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Here's your account overview and recent activity
            </p>
          </div>
          <RefreshButton 
            onRefresh={refreshData}
            isRefreshing={isRefreshing}
            tooltip="Refresh Dashboard"
          />
        </motion.div>

        {/* Top Stats Cards */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {statsData.map((stat) => (
            <SummaryCard
              key={stat.id}
              isLoading={isLoading || isRefreshing}
              label={stat.label}
              value={stat.value}
              subtext={`${stat.subtext} • ${stat.change}`}
              icon={stat.icon}
              iconBgColor={stat.iconBg}
              iconColor={stat.iconColor}
              className={`bg-gradient-to-br ${stat.bgGradient} dark:from-gray-800 dark:to-gray-900`}
            />
          ))}
        </motion.div>

        {/* Order Statistics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 transition-colors duration-300">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Order Statistics</h2>
              <select 
                value={selectedPeriod}
                onChange={(e) => handlePeriodChange(e.target.value)}
                className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 3 months</option>
                <option value="180d">Last 6 months</option>
                <option value="1y">Last year</option>
                <option value="all">All time</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {orderStats.map((stat, index) => (
                <div key={index} className={`${stat.bgColor} dark:bg-gray-800 rounded-xl p-4 text-center border border-gray-100 dark:border-gray-700 transition-colors duration-300`}>
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-lg flex items-center justify-center ${stat.iconColor} bg-white dark:bg-gray-700 shadow-sm transition-colors duration-300`}>
                    {stat.icon}
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{stat.value}</p>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Orders Chart */}
            <ChartContainer 
              title="Order Statistics Overview" 
              isLoading={isLoading}
              className="h-80"
            >
              {!isLoading && orderStats.length > 0 && (
                <ProfessionalBarChart
                  data={orderStats.map(stat => ({
                    label: stat.label,
                    value: stat.value
                  }))}
                  height={250}
                  showValues={true}
                  colors={['#3B82F6', '#10B981', '#EF4444', '#F59E0B']}
                  darkMode={false} // You can connect this to your theme context
                />
              )}
            </ChartContainer>
          </div>
        </motion.div>

        {/* Bottom Section */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {/* Recent Activity */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 transition-colors duration-300">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Activity</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              IMEI Checks: <span className="font-semibold text-gray-900 dark:text-gray-100">
                {dashboardStats?.imei_checks?.total || 0}
              </span>
            </p>
            
            {dashboardStats?.recent_activity && dashboardStats.recent_activity.length > 0 ? (
              <div className="space-y-3">
                {dashboardStats.recent_activity.slice(0, 3).map((activity, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className={`w-2 h-2 rounded-full ${activity.success ? 'bg-success-500' : 'bg-error-500'}`}></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900 dark:text-gray-100">{activity.message}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(activity.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-32 bg-gradient-to-br from-primary-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-xl flex items-center justify-center border border-gray-100 dark:border-gray-700 transition-colors duration-300">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center transition-colors duration-300">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No activity yet</p>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="space-y-4">
            <motion.div 
              onClick={handleInvoicesClick}
              className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 hover:shadow-md dark:hover:shadow-gray-900/40 hover:scale-[1.02] transition-all duration-300 cursor-pointer group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Invoices</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">View your billing history</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              onClick={handleSupportClick}
              className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 hover:shadow-md dark:hover:shadow-gray-900/40 hover:scale-[1.02] transition-all duration-300 cursor-pointer group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/50 transition-colors">
                  <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">Support</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Get help from our team</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
      </DashboardLayout>
    </>
  );
};

export default DashboardHomePage;
